import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    output,
    Renderer2,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { NewsFeedModel } from '../news-feed.model';
import { PostModel } from '@fi-shared/models';
import { IsJsonString } from '@fi-shared/fi-shared-services/extensions.service';
import { ForumService } from '@fi-app/forum/forum.service';
import { DetectDevicesService, PostService } from '@fi-shared/fi-shared-services';
import { NewsFeedItemContentComponent } from '../news-feed-item-content/news-feed-item-content.component';
import { NewsFeedService } from '../news-feed.service';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';
import { NewsFeedItemMenuComponent } from '../news-feed-item-menu/news-feed-item-menu.component';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { AccessRightFilterSettings } from '@fi-shared/access-right-filters';
import { FiNewsFeedEditMobileService } from '../edit-mobile/edit-mobile.service';
import { ModuleType } from '@fi-shared/types/module-type';
import { NewsFeedModuleService } from '../news-feed-module.service';
import { FiCommentSettingType } from '@fi-shared/types';
import { BecomeVisibleDirective } from '@fi-shared/fi-directives/become-visible/become-visible.directive';
import { AccessRightFiltersComponent } from '@fi-shared/access-right-filters/access-right-filters.component';
import { LoadingService } from '@fi-app/shared/fi-loading/loading.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { MediaBankStorageType } from '@fi-shared/media-bank/media-bank-storage-type';
import { FiNewsFeedPinService } from '@fi-shared/fi-news-feed-shared/fi-news-feed-pin.service';
import { INewsFeedMenuItem } from '@fi-app/news-feed/news-feed';
import { FiNewsFeedSharedService } from '@fi-shared/fi-news-feed-shared/fi-news-feed-shared.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiNotificationHelperService } from '@fi-shared/fi-notification/fi-notification-helper.service';
import { takeUntil } from 'rxjs/operators';
import { FiScrollToElementAnimateService } from '@fi-shared/fi-shared-services/scroll-to-element-animate.service';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { isDefined } from '@fi-shared/utils';
import { INewsFeedContent } from '@fi-shared/fi-news-feed-shared/fi-news-feed-shared';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiCommentsListStateService } from '@fi-shared/fi-comments/fi-comments-list/services/fi-comments-list-state.service';
import { FiPrintDownloadService } from '@fi-shared/fi-print-download/fi-print-download.service';
import { TextEditorTagUsersService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-tag-users.service';

@Component({
    selector: 'ccm-news-item',
    templateUrl: './news-feed-item.component.html',
    styleUrls: ['./news-feed-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class NewsFeedItemComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() newsItem: NewsFeedModel;
    @Input() hasDeleteAccess: boolean;
    @Input() showForumData: boolean;
    @Input() highlightCommentId: number;
    @Input() highlightText: string;
    @Input() showWithContentOpened: boolean;
    @Input() showWithCommentsOpened: boolean = false;
    @Input() focusNewComment: boolean;
    @Input() commentsShowLimit: number;
    @Input() shouldScrollIntoItemView: boolean;
    @Input() scrollableElementSelector: string;
    @Input() scrollToCommentId: number;
    public onDelete = output<NewsFeedModel>();

    @ViewChild(NewsFeedItemContentComponent, { static: true }) contentItem: NewsFeedItemContentComponent;
    @ViewChild(NewsFeedItemMenuComponent, { static: false }) itemMenu: NewsFeedItemMenuComponent;
    @ViewChild('fiBecomeVisible', { static: true }) fiBecomeVisible: BecomeVisibleDirective;
    @ViewChild(AccessRightFiltersComponent, { static: false }) accessRightFilters: AccessRightFiltersComponent;
    @ViewChild('addCommentElement', { static: false }) addCommentElement: ElementRef;

    showMenuLoading = false;
    menuItems: INewsFeedMenuItem[];
    currentUserId: number;

    private isForumPost: boolean;

    commentsExpanded = false;

    newsItemMediaBankReferences: IMediaBankFileReference[] = [];

    private anchorClickListener: () => void;
    userMinDateForNotifications: Date;

    constructor(
        private elRef: ElementRef,
        private postService: PostService,
        private forumService: ForumService,
        private newsFeedModuleService: NewsFeedModuleService,
        private mediaBankHelperService: MediaBankHelperService,
        private newsFeedService: NewsFeedService,
        private newsFeedSharedService: FiNewsFeedSharedService,
        private newsFeedEditMobileService: FiNewsFeedEditMobileService,
        private detectDevicesService: DetectDevicesService,
        private loadingService: LoadingService,
        private newsFeedPinService: FiNewsFeedPinService,
        private appTextService: AppTextService,
        private cdr: ChangeDetectorRef,
        private notificationHelperService: FiNotificationHelperService,
        private scrollToElementAnimateService: FiScrollToElementAnimateService,
        private userDataService: FiUserDataService,
        private renderer: Renderer2,
        private fiFormatDateService: FiFormatDateService,
        private commentsListStateService: FiCommentsListStateService,
        private fiPrintDownloadService: FiPrintDownloadService,
        private textEditorTagUsersService: TextEditorTagUsersService,
    ) {
        // run change detection on the component and his children
        /*setTimeout(() => {
            this.cdr.detectChanges();
        }, 1000);*/
    }

    protected isDownloadingDisabled$ = this.fiPrintDownloadService.isDownloadingInProcess$;

    logger() {}

    ngAfterViewInit(): void {
        if (this.shouldScrollIntoItemView) {
            // occurs when user receives '1 new event' at home page
            const scrollableElement: HTMLElement = document.querySelector(this.scrollableElementSelector);
            if (scrollableElement) {
                this.scrollToElementAnimateService.scrollToElementIntoView(this.elRef.nativeElement, scrollableElement);
            }
        }
        setTimeout(() => {
            this.updateCommentsConnectionLine();
        }, 0);
    }

    ngOnChanges(changes: SimpleChanges): void {
        /*const change = changes.hasDeleteAccess;
        if (change && isDefined(change.currentValue))
            this.showMenuLoading = false;*/
        /* if (changes.newsItem) {
             this.contentItem.onAfterSavePost();
         }*/
    }

    ngOnInit() {
        this.currentUserId = this.userDataService.getUserId();
        this.userMinDateForNotifications = new Date(this.userDataService.userData?.MinDateForNotifications);
        this.setNewsItemMediaBankReferences();
        /*
        // we should focus new comment input when we click on 'comments' button in mobile app
        // https://chainformation.atlassian.net/browse/FI-2673
        if ((this.detectDevices.isMobilePhone() || (this.newsItem.InitialComments && this.newsItem.InitialComments.length)) && !this.focusNewComment) {
            this.autofocusAddComment = false;
        }
*/

        // FI-206 Feed improvement - remembering where you were
        // save current post id in current state when clicking on a link in a post
        this.anchorClickListener = this.renderer.listen(this.elRef.nativeElement, 'click', ($event: MouseEvent) => {
            if (($event.target as HTMLElement).tagName === 'A') {
                window.history.replaceState(
                    {
                        remember: {
                            objectId: this.newsItem.ObjectId,
                            objectType: this.newsItem.ObjectType,
                        },
                    },
                    document.title,
                );
            }
        });

        this.updateMenuItems();

        this.isForumPost = this.newsItem.ObjectType == ObjectTypes.ForumPost;

        if (IsJsonString(this.newsItem.Content)) {
            this.newsItem.Content = JSON.parse(<any>this.newsItem.Content);

            //Added check to show comments for post when comment setting type is not set
            if (this.newsItem.ObjectType == ObjectTypes.Post && !this.newsItem.CommentSettingType) {
                this.newsItem.CommentSettingType = FiCommentSettingType.AllowAllComments;
            } else if (!this.newsItem.CommentSettingType) {
                //Added check to not show comments for documents , mediaBank etc when comment setting type is not set
                this.newsItem.CommentSettingType = FiCommentSettingType.NoComments;
            }
            this.newsItem.ShowComments = this.newsItem.CommentSettingType == FiCommentSettingType.AllowAllComments;
        }

        this.commentsExpanded = this.showWithCommentsOpened && !!this.newsItem.InitialComments.length;
        this.updateCommentsConnectionLine();

        this.updateContentWithFixedUserProfileTags();
    }

    private updateCommentsConnectionLine(): void {
        this.commentsListStateService.emitCommentUpdateConnectionLine(
            this.getCommentObjectType(),
            this.getCommentObjectId(),
        );
    }

    protected getCommentsCount(): number {
        return this.commentsListStateService.getCommentsCount(this.getCommentObjectType(), this.getCommentObjectId());
    }

    // special case - media bank file grouped
    // https://chainformation.atlassian.net/browse/FI-4706
    // https://chainformation.atlassian.net/browse/FI-4676

    getCommentObjectId(): number {
        return this.newsFeedSharedService.useNewsFeedItemForComments(this.newsItem)
            ? this.newsItem.Id
            : this.newsItem.ObjectId;
    }

    getCommentObjectType(): ObjectTypes {
        return this.newsFeedSharedService.useNewsFeedItemForComments(this.newsItem)
            ? ObjectTypes.NewsFeed
            : this.newsItem.ObjectType;
    }
    //

    private setNewsItemMediaBankReferences() {
        this.newsItemMediaBankReferences = [...this.newsItem.MediaBankFileReferences];
        if (isDefined(this.newsItem.GroupedItems)) {
            this.newsItemMediaBankReferences = [
                ...this.newsItemMediaBankReferences,
                ...this.newsItem.GroupedItems.map((groupedItem) => {
                    const responseContent: INewsFeedContent = JSON.parse(groupedItem.Content);
                    return this.mediaBankHelperService.getMediaBankFileReferenceFromNewsItemContent(
                        responseContent,
                        groupedItem.ObjectId,
                        this.newsFeedModuleService.GetNameValue(responseContent.Names),
                    );
                }),
            ];
        }
    }

    private updateMenuItems() {
        this.menuItems = [];
        this.menuItems.push({
            title: 'common.edit',
            iconClass: 'fas fa-pencil-alt',
            onClick: () => this.EditorOpen(),
            condition: () => this.newsItem.HasEditAccess,
        });

        this.menuItems.push({
            title: 'post.pin_post',
            iconClass: 'fas fa-thumbtack',
            onClick: () => this.onOpenPinSettingsClick(),
            condition: () =>
                this.newsFeedPinService.canChangePin(this.newsItem.ObjectType) && !this.newsItem.PinnedDate,
        });

        this.menuItems.push({
            title: 'post.pin_settings',
            iconClass: 'fa fa-cog',
            onClick: () => this.onOpenPinSettingsClick(),
            condition: () =>
                this.newsFeedPinService.canChangePin(this.newsItem.ObjectType) && !!this.newsItem.PinnedDate,
        });
        this.menuItems.push({
            title: 'post.remove_pin',
            iconClass: 'fas fa-thumbtack remove-pin',
            onClick: () => this.onRemovePinClick(),
            condition: () =>
                this.newsFeedPinService.canChangePin(this.newsItem.ObjectType) && !!this.newsItem.PinnedDate,
        });

        this.menuItems.push({
            title: 'common.remove',
            iconClass: 'fas fa-trash',
            onClick: () => this.Delete(),
            condition: () => this.hasDeleteAccess,
        });
    }

    hasMenuItems() {
        return !!this.menuItems.find((item) => !item.condition || item.condition());
    }

    onOpenPinSettingsClick() {
        this.newsFeedPinService
            .openPinSettingsDialog(this.newsItem.ObjectId, this.newsItem.ObjectType, this.newsItem.PinnedUntil)
            .then((data) => {
                if (data.result) {
                    this.newsItem.PinnedDate = new Date();
                    this.newsItem.PinnedUntil = data.newPinnedUnitl;
                    this.cdr.markForCheck();
                }
            });
    }

    onRemovePinClick() {
        this.newsFeedPinService.removePin(this.newsItem.ObjectId, this.newsItem.ObjectType).then(() => {
            this.newsItem.PinnedUntil = undefined;
            this.newsItem.PinnedDate = undefined;
            this.cdr.markForCheck();
        });
    }

    showLikes() {
        return this.newsItem.ShowReactions;
    }

    getCommentsCountLabel(commentsCount: number): string {
        return (
            commentsCount +
            ' ' +
            this.appTextService.get(commentsCount === 1 ? 'common.comment' : 'common.comments').toLowerCase()
        );
    }

    EditorOpen() {
        if (this.editorPost$.value) return false;

        this._getEditorOpenData().then((data) => {
            const EditorPost = new PostModel(this.fiFormatDateService, data, this.isForumPost);

            if (this.detectDevicesService.isMobilePhone()) {
                this.newsFeedEditMobileService
                    .openMobilePostEditor(EditorPost)
                    .afterClosed()
                    .pipe(takeUntil(this.ngOnDestroy$))
                    .subscribe((result) => {
                        if (result) {
                            this.newsFeedModuleService
                                .savePost(EditorPost, this.accessRightFilters)
                                .then((saveResult) => {
                                    this.AfterSavePost(saveResult);
                                })
                                .catch(() => {});
                        }
                    });
            } else {
                this._addEditor(EditorPost);
                this.renderer.addClass(this.elRef.nativeElement, 'editor-opened');
            }
        });
    }

    _getEditorOpenData(): Promise<any> {
        return this.isForumPost
            ? this.forumService.getForumPost(this.newsItem.ObjectId)
            : this.postService.Get(this.newsItem.ObjectId);
    }

    private updateContentWithFixedUserProfileTags() {
        const objectType = this.newsItem.ObjectType;
        if (objectType === ObjectTypes.Post || objectType === ObjectTypes.ForumPost) {
            const updatedContent = this.textEditorTagUsersService.getContentWithFixedUserProfileTags(
                this.newsItem.Content.Body,
                this.newsItem.TaggedUsers,
            );
            this.newsItem.Content.Body = updatedContent;
        }
    }

    AfterSavePost(savePost: PostModel) {
        this.newsItem.Content.Body = savePost.Content;
        this.newsItem.Links = savePost.Links;
        this.newsItem.MediaBankFileReferences = savePost.MediaBankFileReferences;
        this.newsItem.TaggedUsers = savePost.TaggedUsers;
        this.updateContentWithFixedUserProfileTags();
        this.setNewsItemMediaBankReferences();

        this._removeEditor();
        this.renderer.removeClass(this.elRef.nativeElement, 'editor-opened');

        this.contentItem.onAfterSavePost();
    }

    onCancelPostEdit(): void {
        this.renderer.removeClass(this.elRef.nativeElement, 'editor-opened');
        this._removeEditor();
    }

    editorPost$ = new BehaviorSubject<PostModel>(null);
    editorPostAccessRightFilterSettings: AccessRightFilterSettings;

    _addEditor(editorPost: PostModel) {
        this.editorPost$.next(editorPost);
        this.editorPostAccessRightFilterSettings = new AccessRightFilterSettings(
            ModuleType.Post,
            false,
            true,
            ObjectTypes.Post,
        );
    }

    _removeEditor() {
        this.editorPost$.next(null);
        this.editorPostAccessRightFilterSettings = undefined;
    }

    Delete() {
        this.onDelete.emit(this.newsItem);
    }

    ToggleShowComment() {
        this.commentsExpanded = !this.commentsExpanded;
        this.updateCommentsConnectionLine();
    }

    IsDownloadable() {
        return (
            this.newsItem.ObjectType === ObjectTypes.MediaBank ||
            this.newsItem.ObjectType == ObjectTypes.MediaBankGrouped
        );
    }

    DownloadFiles($event) {
        if (this.newsItemMediaBankReferences.length > 0) {
            let idx = 0;
            const checkDownload = () => {
                if (idx < this.newsItemMediaBankReferences.length) {
                    const link = this.newsItemMediaBankReferences[idx];
                    this.downloadMediaBankFile(
                        link.ObjectId,
                        link.Name,
                        link.ContentType,
                        link.Extension,
                        link.PhysicalFileName,
                        link.FileType,
                        link.StorageType,
                        $event,
                    );
                    setTimeout(() => {
                        idx++;
                        checkDownload();
                    }, 500);
                }
            };
            checkDownload();
        } else {
            this.downloadMediaBankFile(
                this.newsItem.ObjectId,
                this.newsFeedModuleService.GetNameValue(this.newsItem.Content.Names),
                this.newsItem.Content.ContentType,
                this.newsItem.Content.Extension,
                this.newsItem.Content.PhysicalFileName,
                this.newsItem.Content.FileType,
                this.newsItem.Content.StorageType,
                $event,
            );
        }
    }

    private downloadMediaBankFile(
        mediaBankFileId: number,
        mediaBankFileName: string,
        mediaBankFileContentType: string,
        mediaBankFileExtension: string,
        mediaBankFilePhysicalFileName: string,
        mediaBankFileFileType: number,
        mediaBankFileStorageType: MediaBankStorageType,
        $event,
    ): Promise<void> {
        $event.stopPropagation();
        const promise = this.mediaBankHelperService.downloadMediaBankFile(
            {
                Id: mediaBankFileId,
                Name: mediaBankFileName,
                ContentType: mediaBankFileContentType,
                Extension: mediaBankFileExtension.replace(/mov/gi, 'mp4'),
                PhysicalFileName: mediaBankFilePhysicalFileName,
                FileType: mediaBankFileFileType,
                StorageType: mediaBankFileStorageType,
            },
            {
                //allowMultipleDownloads: true,
            },
        );
        return promise;
    }

    getCurrentUser() {
        return this.userDataService.userData;
    }

    showSiteDesignAccessForItems() {
        return this.userDataService.showSiteDesignAccessForItems();
    }

    onNewsFeedMarkedAsVisited() {
        //this.cdr.detectChanges();
        if (this.notificationHelperService.isNotVisited(this.newsItem)) {
            // FI-1207 if item was marked as read dont decrease badge
            this.newsFeedService.OnMarkNewsFeedAsVisited(this.newsItem);
        }
        this.notificationHelperService.setIsVisited(this.newsItem);
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
        this.anchorClickListener();
        /*
                if (this.closeCommentOnDestroy) {
                    this.newsFeedService.ShowComment = false;
                }
        */
    }

    showBrandedBorder() {
        return (
            this.userMinDateForNotifications <= this.newsItem.ChangedOn &&
            this.notificationHelperService.isNotVisited(this.newsItem)
        );
    }

    showBrandedBackground() {
        return (
            this.userMinDateForNotifications <= this.newsItem.ChangedOn &&
            this.notificationHelperService.isNotSeen(this.newsItem)
        );
    }
}
